import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { AddressAutofill } from '@mapbox/search-js-react';
import 'react-phone-input-2/lib/style.css';
import Flatpickr from 'react-flatpickr';
import { useAuth } from '../../context/AuthContext';

const requiredFields = [
  'address_line_1',
  'city',
  'dob',
  'firstName',
  'lastName',
  'phone_number',
  'postal_code',
  'state',
];
const PersonalInformation = forwardRef(
  ({ newApplicant, setNewApplicant }, ref) => {
    const { user } = useAuth();
    const [errors, setErrors] = useState({});
    useImperativeHandle(ref, () => ({
      validateForm: () => {
        const newErrors = {};
        requiredFields.forEach((field) => {
          if (!newApplicant?.personalInformation?.[field]?.trim()) {
            newErrors[field] = 'This field is required';
          }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      },
    }));
    const handleChange = (field, value) => {
      if (field === 'phone_number') {
        const cleaned = value.replace(/\D/g, '');
        let error = '';
        if (cleaned.length > 0 && cleaned.length !== 10) {
          error = 'Enter a valid 10-digit US phone number';
        }
        setNewApplicant((prev) => ({
          ...prev,
          personalInformation: {
            ...prev.personalInformation,
            [field]: value,
          },
        }));
        setErrors((prev) => ({ ...prev, [field]: error }));
      } else {
        setNewApplicant((prev) => ({
          ...prev,
          personalInformation: {
            ...prev.personalInformation,
            [field]: value,
          },
        }));
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    };

    const addressChangeFn = (address) => {
      setNewApplicant((prev) => ({
        ...prev,
        personalInformation: {
          ...prev.personalInformation,
          address_line_1: address.features[0].properties.address_line1,
          address_line_2: address.features[0].properties.address_line2,
          city: address.features[0].properties.address_level2,
          state: address.features[0].properties.address_level1,
          postal_code: address.features[0].properties.postcode,
        },
      }));
      setErrors((prev) => ({
        ...prev,
        address_line_1: '',
        city: '',
        state: '',
        postal_code: '',
      }));
    };

    useEffect(() => {
      if (user) {
        // Users only store full_name, so split it to prefill first/last name
        const [firstName = '', ...rest] = (user.full_name || '')
          .trim()
          .split(/\s+/);
        setNewApplicant((prev) => ({
          ...prev,
          personalInformation: {
            ...prev.personalInformation,
            firstName,
            lastName: rest.join(' '),
            email: user.email,
            phone_number: user.phone_number,
            address_line_1: user.address_line_1,
            address_line_2: user.address_line_2,
            city: user.city,
            state: user.state,
            postal_code: user.postal_code,
            dob: user.dob ? new Date(user.dob).toISOString() : '',
            referral_code: user.referral_code,
          },
        }));
      }
    }, [setNewApplicant]);

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`form-input w-full bg-gray-100 rounded-md ${
                errors?.firstName ? 'border border-red-500' : ''
              }`}
              value={newApplicant?.personalInformation?.firstName || ''}
              onChange={(e) => handleChange('firstName', e.target.value)}
            />
            {errors?.firstName && (
              <div className="text-red-500 text-xs mt-1">
                {errors?.firstName}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`form-input w-full bg-gray-100 rounded-md ${
                errors?.lastName ? 'border border-red-500' : ''
              }`}
              value={newApplicant?.personalInformation?.lastName || ''}
              onChange={(e) => handleChange('lastName', e.target.value)}
            />
            {errors?.lastName && (
              <div className="text-red-500 text-xs mt-1">
                {errors?.lastName}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm mb-1">
              DOB <span className="text-red-500">*</span>
            </label>
            <Flatpickr
              value={
                newApplicant?.personalInformation?.dob
                  ? new Date(newApplicant.personalInformation.dob)
                  : null
              }
              onChange={([date]) => {
                setNewApplicant((prev) => ({
                  ...prev,
                  personalInformation: {
                    ...prev.personalInformation,
                    dob: date?.toISOString() || '',
                  },
                }));
              }}
              options={{
                dateFormat: 'm/d/Y',
                altInput: true,
                altFormat: 'm/d/Y',
                maxDate: 'today',
              }}
              className="form-input w-full bg-gray-100 rounded-md"
              placeholder="MM/DD/YYYY"
            />

            {errors?.dob && (
              <div className="text-red-500 text-xs mt-1">{errors?.dob}</div>
            )}
          </div>
          <div>
            <label className="block text-sm mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              className={`w-full form-input bg-portal-100 font-medium text-portal-900 ${
                errors?.phone_number ? 'border border-red-500' : ''
              }`}
              value={newApplicant?.personalInformation?.phone_number || ''}
              onChange={(e) => handleChange('phone_number', e.target.value)}
            />
            {errors?.phone_number && (
              <div className="text-red-500 text-xs mt-1">
                {errors?.phone_number}
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm mb-1">Referral Code</label>
            <input
              type="text"
              className={`form-input w-full bg-gray-100 rounded-md ${
                errors?.referral_code ? 'border border-red-500' : ''
              }`}
              value={newApplicant?.personalInformation?.referral_code || ''}
              onChange={(e) => handleChange('referral_code', e.target.value)}
            />
            {errors?.referral_code && (
              <div className="text-red-500 text-xs mt-1">
                {errors?.referral_code}
              </div>
            )}
          </div>
        </div>
        <div className="pt-4">
          <h3 className="text-md font-semibold mb-3">Home Address</h3>

          <AddressAutofill
            accessToken="pk.eyJ1IjoiZ2lkZHlvcHMiLCJhIjoiY2w2Z3BhOTh1MDA4ZzNrbW1ya2N2cjhhOSJ9.gRglIHmbBz4nUr79eUgbJA"
            options={{
              language: 'en',
              country: 'US',
            }}
            onRetrieve={addressChangeFn}>
            <div className="space-y-6">
              <div>
                <label className="block text-sm mb-1">
                  Street Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="address-line1"
                  autoComplete="address-line1"
                  className={`form-input w-full bg-gray-100 rounded-md ${
                    errors?.address_line_1 ? 'border border-red-500' : ''
                  }`}
                  value={
                    newApplicant?.personalInformation?.address_line_1 || ''
                  }
                  onChange={(e) =>
                    handleChange('address_line_1', e.target.value)
                  }
                />
                {errors?.address_line_1 && (
                  <div className="text-red-500 text-xs mt-1">
                    {errors?.address_line_1}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1">Apt/Suite/Unit</label>
                <input
                  type="text"
                  name="address-line2"
                  autoComplete="address-line2"
                  className={`form-input w-full bg-gray-100 rounded-md ${
                    errors?.address_line_2 ? 'border border-red-500' : ''
                  }`}
                  value={
                    newApplicant?.personalInformation?.address_line_2 || ''
                  }
                  onChange={(e) =>
                    handleChange('address_line_2', e.target.value)
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm mb-1">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address-level2"
                    autoComplete="address-level2"
                    className={`form-input w-full bg-gray-100 rounded-md ${
                      errors?.city ? 'border border-red-500' : ''
                    }`}
                    value={newApplicant?.personalInformation?.city || ''}
                    onChange={(e) => handleChange('city', e.target.value)}
                  />
                  {errors?.city && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors?.city}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address-level1"
                    autoComplete="address-level1"
                    className={`form-input w-full bg-gray-100 rounded-md ${
                      errors?.state ? 'border border-red-500' : ''
                    }`}
                    value={newApplicant?.personalInformation?.state || ''}
                    onChange={(e) => handleChange('state', e.target.value)}
                  />
                  {errors?.state && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors?.state}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm mb-1">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="postal-code"
                    autoComplete="postal-code"
                    className={`form-input w-full bg-gray-100 rounded-md ${
                      errors?.postal_code ? 'border border-red-500' : ''
                    }`}
                    value={newApplicant?.personalInformation?.postal_code || ''}
                    onChange={(e) =>
                      handleChange('postal_code', e.target.value)
                    }
                  />
                  {errors?.postal_code && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors?.postal_code}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </AddressAutofill>
        </div>
        {/* Example button to trigger validation */}
        {/* <button onClick={validate} className="btn btn-primary mt-4">Next</button> */}
      </div>
    );
  }
);

export default PersonalInformation;
